import { User } from '../@types';
import {
  canAccessProductRoute,
  getProductFallbackPath,
  getProductHomePath,
  getProductRuleAccess,
  hasAnyIpaProduct,
  hasNegotiation,
  ProductRuleAccess,
} from './ipaProductAccess';

export type IpaRouteAccess = 'NEGOTIATION' | 'CAMPAIGNS' | 'PRICE_OPTIMIZATION' | 'DEMAND_FORECAST' | 'PRICE_ARCHITECTURE' | 'SDD' | 'PRO';

export const IPA_ADDITIONAL_CONTRACTS = {
  NEGOTIATION: 'MODULO_NEGOCIACAO',
  CAMPAIGNS: 'CAMPANHAS_PROMOCIONAIS',
  PRICE_OPTIMIZATION: 'OTIMIZACAO_DE_PRECOS',
  DEMAND_FORECAST: 'PREVISAO_DEMANDA',
  PRICE_ARCHITECTURE: 'ARQUITETURA_PRECOS',
} as const;

export const IPA_NEGOTIATION_HOME_PATH = '/ipa/negociacoes/fornecedor';
export const IPA_PRO_HOME_PATH = '/ipa/gerenciador-de-precos';
export const IPA_PRICE_ARCHITECTURE_PATH = '/ipa/arquitetura-precos';
export const SDD_HOME_PATH = '/ipa/calculadora-demanda';

export const isProductRule = (user?: User | null) => user?.accessRule === 'PRODUCT';

export const hasResolvedAccessRule = (user?: User | null) => user?.accessRule === 'PRODUCT' || user?.accessRule === 'PACKAGE';

// Texto unico do bloqueio da Previsao de Demanda: o card de Simulacoes e Analises
// e a celula do Gerenciador mostram exatamente a mesma mensagem.
export const DEMAND_FORECAST_UPSELL_MESSAGE =
  'A Previsão de Demanda está disponível no plano Enterprise. Converse com o time de Atendimento para saber como incluir no seu contrato.';

// O BASIC e uma excecao dentro do IPA: ele acessa somente as rotas marcadas
// como NEGOTIATION, mesmo sem adicional de contrato, mas continua bloqueado
// para a area PRO mais ampla. Os helpers abaixo deixam essa separacao explicita
// para o guard de rota nao tratar todo `/ipa/*` como a mesma permissao.
export const isIpaBasicPackage = (user?: User | null) => user?.servicoIpa?.pacoteIpa === 'BASIC';

export const hasIpaAdditionalContract = (user: User | null | undefined, contract: string) => {
  return (user?.tipoAdicionalContratos?.IPA ?? []).includes(contract);
};

export const hasService = (user: User | null | undefined, serviceName: string) => {
  return !!user?.servicos?.some(service => service.name === serviceName) || !!user?.servicosCliente?.some(service => service.name === serviceName);
};

// Negociacao tem regra propria:
// - BASIC acessa as rotas de negociacao porque esse e o modulo do pacote.
// - PRO so acessa negociacao quando MODULO_NEGOCIACAO esta nos adicionais.
// Por isso negociacao nao pode cair na regra generica de rotas PRO.
export const canAccessIpaNegotiation = (user?: User | null) => {
  if (isProductRule(user)) return hasNegotiation(user);
  return hasService(user, 'IPA') && (isIpaBasicPackage(user) || hasIpaAdditionalContract(user, IPA_ADDITIONAL_CONTRACTS.NEGOTIATION));
};

// Campanhas tambem tem regra propria:
// - BASIC nunca acessa campanhas.
// - PRO so acessa campanhas quando CAMPANHAS_PROMOCIONAIS esta nos adicionais.
// Esse helper evita abrir URLs de campanhas para todo usuario nao BASIC.
export const canAccessIpaCampaigns = (user?: User | null) => {
  if (isProductRule(user)) return canAccessProductRoute(user, 'CAMPAIGNS');
  return hasService(user, 'IPA') && !isIpaBasicPackage(user) && hasIpaAdditionalContract(user, IPA_ADDITIONAL_CONTRACTS.CAMPAIGNS);
};

// Otimizacao de precos segue o mesmo formato de campanhas:
// - BASIC nunca acessa.
// - PRO so acessa quando OTIMIZACAO_DE_PRECOS esta nos adicionais do contrato.
export const canAccessIpaPriceOptimization = (user?: User | null) => {
  if (isProductRule(user)) return canAccessProductRoute(user, 'PRICE_OPTIMIZATION');
  return hasService(user, 'IPA') && !isIpaBasicPackage(user) && hasIpaAdditionalContract(user, IPA_ADDITIONAL_CONTRACTS.PRICE_OPTIMIZATION);
};

// Previsao de demanda segue o mesmo formato de otimizacao de precos:
// - BASIC nunca acessa.
// - PRO so acessa quando PREVISAO_DEMANDA esta nos adicionais do contrato.
// - Sem dados de adicionais no usuario, o acesso fica bloqueado.
export const canAccessIpaDemandForecast = (user?: User | null) => {
  if (isProductRule(user)) return canAccessProductRoute(user, 'DEMAND_FORECAST');
  return hasService(user, 'IPA') && !isIpaBasicPackage(user) && hasIpaAdditionalContract(user, IPA_ADDITIONAL_CONTRACTS.DEMAND_FORECAST);
};

// Arquitetura de precos segue o mesmo formato de campanhas:
// - BASIC nunca acessa.
// - PRO so acessa quando ARQUITETURA_PRECOS esta nos adicionais do contrato,
//   o mesmo adicional que libera o item no Setup IPA do admin.
export const canAccessIpaPriceArchitecture = (user?: User | null) => {
  if (isProductRule(user)) return canAccessProductRoute(user, 'PRICE_ARCHITECTURE');
  return hasService(user, 'IPA') && !isIpaBasicPackage(user) && hasIpaAdditionalContract(user, IPA_ADDITIONAL_CONTRACTS.PRICE_ARCHITECTURE);
};

export const canAccessIpaRoute = (user: User | null | undefined, routeAccess?: IpaRouteAccess) => {
  if (isProductRule(user)) return canAccessProductRoute(user, routeAccess);
  if (!routeAccess) return true;
  if (!user) return false;
  if (routeAccess === 'NEGOTIATION') return canAccessIpaNegotiation(user);
  if (routeAccess === 'CAMPAIGNS') return canAccessIpaCampaigns(user);
  if (routeAccess === 'PRICE_OPTIMIZATION') return canAccessIpaPriceOptimization(user);
  if (routeAccess === 'DEMAND_FORECAST') return canAccessIpaDemandForecast(user);
  if (routeAccess === 'PRICE_ARCHITECTURE') return canAccessIpaPriceArchitecture(user);
  if (routeAccess === 'SDD') return hasService(user, 'SDD');
  return hasService(user, 'IPA') && !isIpaBasicPackage(user);
};

// A Negociacao manual (o simulador tributario) mora fora de /ipa/, mas e liberada
// pela mesma regra de Negociacao fornecedor e Negociacoes salvas.
const IPA_NEGOTIATION_ROUTE_PATHS = ['/ipa/negociacoes/fornecedor/', '/ipa/negociacoes/salvas/', '/ipa/negociacoes/salvas/:id', '/simulador', '/simulador/:id'];
const SDD_ROUTE_PATHS = ['/ipa/calculadora-demanda', '/ipa/cenarios-salvos', '/ipa/comparacao-cenarios'];
// Inclui as rotas da versao legada (v1) para nao deixar a previsao aberta por URL antiga.
// O hub /ipa/ia/simulacoes-analises fica fora: ele continua na regra PRO.
const DEMAND_FORECAST_ROUTE_PATHS = [
  '/ipa/simulacoes-analises/previsao-demanda',
  '/ipa/simulacoes-analises/previsao-demanda/lista',
  '/ipa/simulacoes-analises/previsao-demanda/:id',
  '/ipa/ia/simulacoes-analises/previsao-demanda-v2',
  '/ipa/ia/simulacoes-analises/previsao-demanda-v2/lista',
  '/ipa/ia/simulacoes-analises/previsao-demanda-v2/:id',
];

// Diz qual regra de acesso a guarda de rota aplica a cada caminho configurado.
// Caminho sem regra devolve undefined: a rota fica so com a exigencia de login.
export const getIpaRouteAccessByPath = (path: string): IpaRouteAccess | undefined => {
  if (IPA_NEGOTIATION_ROUTE_PATHS.includes(path)) return 'NEGOTIATION';
  if (SDD_ROUTE_PATHS.includes(path)) return 'SDD';
  if (path.startsWith('/ipa/campanhas')) return 'CAMPAIGNS';
  if (path.startsWith('/ipa/ia/otimizacao-de-precos')) return 'PRICE_OPTIMIZATION';
  if (DEMAND_FORECAST_ROUTE_PATHS.includes(path)) return 'DEMAND_FORECAST';
  if (path.startsWith(IPA_PRICE_ARCHITECTURE_PATH)) return 'PRICE_ARCHITECTURE';
  if (path.startsWith('/ipa/')) return 'PRO';
  return undefined;
};

const PRO_HOME_FALLBACK_ROUTE_ACCESSES: ReadonlySet<IpaRouteAccess> = new Set([
  'NEGOTIATION',
  'CAMPAIGNS',
  'PRICE_OPTIMIZATION',
  'DEMAND_FORECAST',
  'PRICE_ARCHITECTURE',
]);

// Fallbacks levam o usuario para a area IPA mais adequada ao perfil dele:
// - BASIC bloqueado em PRO ou CAMPAIGNS volta para negociacao, que e a home
//   dele dentro do IPA.
// - PRO bloqueado em rota com adicional proprio (NEGOTIATION, CAMPAIGNS,
//   PRICE_OPTIMIZATION, DEMAND_FORECAST, PRICE_ARCHITECTURE) volta para a home PRO generica.
export const getIpaRouteFallbackPath = (user: User | null | undefined, routeAccess?: IpaRouteAccess) => {
  if (isProductRule(user)) return getProductFallbackPath(user, routeAccess);
  if (!hasService(user, 'IPA')) {
    if (hasService(user, 'SDD')) return SDD_HOME_PATH;
    if (hasService(user, 'PAN')) return user?.servicoPanel?.pacotePainel?.name === 'FREE' ? '/isa/painel-geral-free' : '/isa/painel-geral';
    if (hasService(user, 'COL')) return '/isa/historico-preco';
    if (hasService(user, 'IRA')) return '/ira';
    if (hasService(user, 'BI') && user?.servicoBi?.analises?.length) return `/analise/${user.servicoBi.analises[0]?.codigo}`;
    if (hasService(user, 'BI') && user?.servicoBi?.graficos?.length) return `/acompanhamento/${user.servicoBi.graficos[0]?.codigo}`;
    return '/login';
  }

  if (isIpaBasicPackage(user) || !routeAccess) return IPA_NEGOTIATION_HOME_PATH;

  return PRO_HOME_FALLBACK_ROUTE_ACCESSES.has(routeAccess) ? IPA_PRO_HOME_PATH : IPA_NEGOTIATION_HOME_PATH;
};

const hasOwnIpaService = (user?: User | null) => !!user?.servicos?.some(service => service.name === 'IPA');

export const getIpaHomePath = (user?: User | null): string | null => {
  if (isProductRule(user)) return getProductHomePath(user);
  if (!hasOwnIpaService(user)) return null;
  return isIpaBasicPackage(user) ? IPA_NEGOTIATION_HOME_PATH : IPA_PRO_HOME_PATH;
};

export const getProductAccess = (user?: User | null): ProductRuleAccess => {
  if (isProductRule(user)) return getProductRuleAccess(user);
  return {
    hasPricing: canAccessIpaRoute(user, 'PRO'),
    hasNegotiation: canAccessIpaNegotiation(user),
    clientHasPricing: hasService(user, 'IPA'),
    clientHasNegotiation: hasIpaAdditionalContract(user, IPA_ADDITIONAL_CONTRACTS.NEGOTIATION),
  };
};

export const canOpenIpaSettings = (user?: User | null) => {
  if (isProductRule(user)) return hasAnyIpaProduct(user);
  return hasOwnIpaService(user);
};

export const shouldLoadIpaSetup = (user?: User | null) => {
  if (isProductRule(user)) return hasAnyIpaProduct(user);
  return hasOwnIpaService(user);
};
